import React, { useState, useEffect, useRef } from 'react';
import ListingService from '../services/ListingService';

const PropertyDetailModal = ({ property, agent, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [readMoreExpanded, setReadMoreExpanded] = useState(false);
  const [showMortgageModal, setShowMortgageModal] = useState(false);

  const thumbnailRefs = useRef([]);

  const formatDetailImageUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    return url
      .replace(/\/images\/items\/sm\//g, '/images/items/md/')
      .replace(/\/images\/sm\//g, '/images/md/')
      .replace(/\/items\/sm\//g, '/items/md/');
  };

  const mainImage = formatDetailImageUrl(property?.imageUrl);
  const rawGallery = (Array.isArray(property?.galleryImages) && property.galleryImages.length > 0)
    ? property.galleryImages
    : (mainImage ? [mainImage] : []);
  const gallery = rawGallery.map(img => formatDetailImageUrl(img));

  // Auto-scroll the active thumbnail into view when currentImageIndex changes
  useEffect(() => {
    if (thumbnailRefs.current[currentImageIndex]) {
      thumbnailRefs.current[currentImageIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [currentImageIndex]);

  // Prevent background scrolling & allow closing modal on Escape, and photo nav on ArrowLeft / ArrowRight keypresses
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentImageIndex((prev) => (gallery.length > 0 ? (prev + 1) % gallery.length : 0));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentImageIndex((prev) => (gallery.length > 0 ? (prev - 1 + gallery.length) % gallery.length : 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, gallery.length]);

  // Update document title & meta tags for SEO and Google Indexing when viewing property detail
  useEffect(() => {
    if (!property) return;
    const prevTitle = document.title;
    const propTitle = property.title || "Property Details";
    const propTitleCh = property.titleCh || property.title_ch || "";
    const propArea = property.area || "Johor Bahru";
    const propPrice = property.price || "";
    const propLocation = property.location || `${propArea}, Johor Bahru`;
    
    // Construct rich, multilingual SEO Title containing both English and Chinese titles
    const seoTitle = `${propTitle}${propTitleCh ? ' | ' + propTitleCh : ''} in ${propArea} (${propPrice}) | ${agent?.name || 'Nicole Pua'} Property Agent`;
    document.title = seoTitle;

    // Update or create Meta Description for Search Engine Crawlers & Google Indexing
    let metaDesc = document.querySelector('meta[name="description"]');
    const oldMetaDescContent = metaDesc ? metaDesc.getAttribute('content') : '';
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }

    // Build structured meta description with relevant item parameters, strictly avoiding rawDesc
    const descParts = [];
    if (propTitleCh) {
      descParts.push(propTitleCh);
    }
    descParts.push(`${propTitle} located in ${propLocation}.`);
    if (propPrice) {
      descParts.push(`Priced at ${propPrice}.`);
    }
    
    const specs = [];
    if (property.propertyType) specs.push(property.propertyType);
    if (property.bedrooms) specs.push(`${property.bedrooms} Bedrooms`);
    if (property.bathrooms) specs.push(`${property.bathrooms} Bathrooms`);
    if (property.builtUp || property.builtup) specs.push(`Built-up: ${property.builtUp || property.builtup}`);
    if (property.landArea || property.size) specs.push(`Land Size: ${property.landArea || property.size}`);
    if (property.tenure) specs.push(property.tenure);
    if (property.lotType) specs.push(property.lotType);
    if (property.facing || property.direction) specs.push(`Facing: ${property.facing || property.direction}`);
    
    const hasGated = property.gatedGuarded || property.gated_guarded;
    if (hasGated && (hasGated.toLowerCase().includes('yes') || hasGated.toLowerCase().includes('y') || hasGated.toLowerCase().includes('gate'))) {
      specs.push("Gated & Guarded");
    }
    if (specs.length > 0) {
      descParts.push(specs.join(', ') + '.');
    }
    descParts.push(`Contact ${agent?.name || 'Nicole Pua'} for professional assistance and to schedule a viewing.`);
    
    const seoDesc = descParts.join(' ');
    metaDesc.setAttribute('content', seoDesc);

    // Update Open Graph tags for social sharing
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', seoTitle);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    const oldOgDescContent = ogDesc ? ogDesc.getAttribute('content') : '';
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', seoDesc);

    let ogImage = document.querySelector('meta[property="og:image"]');
    const oldOgImageContent = ogImage ? ogImage.getAttribute('content') : '';
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    const metaImageUrl = mainImage || property.imageUrl;
    if (metaImageUrl) {
      const fullImageUrl = metaImageUrl.startsWith('http') 
        ? metaImageUrl 
        : `${window.location.origin}${metaImageUrl}`;
      ogImage.setAttribute('content', fullImageUrl);
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    const oldOgUrlContent = ogUrl ? ogUrl.getAttribute('content') : '';
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', window.location.href);

    // Update Twitter Card tags for Twitter/X sharing
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.name = 'twitter:card';
      twitterCard.content = 'summary_large_image';
      document.head.appendChild(twitterCard);
    }
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.name = 'twitter:title';
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', seoTitle);

    let twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDesc) {
      twitterDesc = document.createElement('meta');
      twitterDesc.name = 'twitter:description';
      document.head.appendChild(twitterDesc);
    }
    twitterDesc.setAttribute('content', seoDesc);

    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.name = 'twitter:image';
      document.head.appendChild(twitterImage);
    }
    if (metaImageUrl) {
      const fullImageUrl = metaImageUrl.startsWith('http') 
        ? metaImageUrl 
        : `${window.location.origin}${metaImageUrl}`;
      twitterImage.setAttribute('content', fullImageUrl);
    }

    return () => {
      document.title = prevTitle;
      if (metaDesc && oldMetaDescContent) {
        metaDesc.setAttribute('content', oldMetaDescContent);
      }
      if (ogDesc) {
        if (oldOgDescContent) ogDesc.setAttribute('content', oldOgDescContent);
        else ogDesc.remove();
      }
      if (ogImage) {
        if (oldOgImageContent) ogImage.setAttribute('content', oldOgImageContent);
        else ogImage.remove();
      }
      if (ogUrl) {
        if (oldOgUrlContent) ogUrl.setAttribute('content', oldOgUrlContent);
        else ogUrl.remove();
      }
      if (twitterCard) twitterCard.remove();
      if (twitterTitle) twitterTitle.remove();
      if (twitterDesc) twitterDesc.remove();
      if (twitterImage) twitterImage.remove();
    };
  }, [property, agent]);

  if (!property) return null;

  // Build property details with fallback defaults matching standard Malaysian property specs
  const title = property.title || "Property Details";
  const listingType = property.listingType || "FOR SALE";
  const price = property.price || "";
  const isRent = Boolean(
    (property.listingType && property.listingType.toLowerCase().includes('rent')) ||
    (property.type && property.type.toLowerCase().includes('rent')) ||
    (property.itemType && property.itemType.toLowerCase().includes('rent')) ||
    (property.item_type && property.item_type.toLowerCase().includes('rent')) ||
    (typeof property.price === 'string' && (
      property.price.toLowerCase().includes('/ mth') ||
      property.price.toLowerCase().includes('/mo') ||
      property.price.toLowerCase().includes('month')
    ))
  );
  const area = property.area || "Johor Bahru";
  const location = property.location || `${area}, Johor Bahru`;
  const itemId = property.id || property.itemId;
  const listedDate = property.date || property.listedOn || property.listedDate || property.createdDate || property.createdAt || property.dateListed;

  const totalPhotosCount = property.totalPhotos || gallery.length;

  const hasValue = (val) => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    return str !== '' && str !== '-' && str !== 'null' && str !== 'undefined';
  };

  const capitalizeFirstChar = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val).trim();
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const candidateDetailsList = [
    { label: "Bedrooms", value: capitalizeFirstChar(property.bedrooms), icon: "bi-door-closed" },
    { label: "Bathrooms", value: capitalizeFirstChar(property.bathrooms), icon: "bi-droplet" },
    { label: "Carpark", value: capitalizeFirstChar(property.carpark), icon: "bi-car-front" },
    { label: "Built-up", value: capitalizeFirstChar(property.builtUp || property.builtup), icon: "bi-arrows-angle-expand" },
    { label: "Land Size", value: capitalizeFirstChar(property.landArea || property.landSize || property.size), icon: "bi-aspect-ratio" },
    { label: "Tenure", value: capitalizeFirstChar(property.tenure), icon: "bi-shield-check" },
    { label: "Gated & Guarded", value: capitalizeFirstChar(property.gatedGuarded || property.gated_guarded), icon: "bi-shield-lock" },
    { label: "Ownership", value: capitalizeFirstChar(property.ownershipType || property.ownership), icon: "bi-award" },
    { label: "Property Type", value: capitalizeFirstChar(property.propertyType), icon: "bi-house-door" },
    { label: "Lot Type", value: capitalizeFirstChar(property.lotType), icon: "bi-tag" },
    { label: "Facing", value: capitalizeFirstChar(property.facing || property.direction), icon: "bi-compass" },
  ];

  const propertyDetailsList = candidateDetailsList.filter((item) => hasValue(item.value));

  // Compute dynamic tags from item DB fields
  const rawTags = [];
  if (Array.isArray(property.tags) && property.tags.length > 0) {
    rawTags.push(...property.tags);
  } else {
    if (hasValue(property.tenure)) rawTags.push(property.tenure);
    if (hasValue(property.lotType)) rawTags.push(property.lotType);
    if (hasValue(property.ownershipType)) rawTags.push(property.ownershipType);
    if (hasValue(property.tag1)) rawTags.push(property.tag1);
    if (hasValue(property.tag2)) rawTags.push(property.tag2);
    if (hasValue(property.tag3)) rawTags.push(property.tag3);
  }
  const finalTags = Array.from(new Set(rawTags.map(t => capitalizeFirstChar(t)).filter(hasValue)));
  const descriptionText = property.remarks || property.description ||
    `Well-maintained property located in the heart of ${area}.\n\n` +
    `This home offers a practical layout, spacious living area, dry and wet kitchen, and a comfortable yard space—perfect for families.\n\n` +
    `Conveniently located near top amenities, schools, parks, and major highways.\n\n` +
    `Move-in ready and ideal for owner-occupation.`;

  const descriptionParagraphs = descriptionText.split('\n\n').filter(p => p.trim().length > 0);
  const hasMoreContent = descriptionParagraphs.length > 1 || (descriptionParagraphs.length === 1 && descriptionParagraphs[0].length > 250);

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleNextImage = () => {
    if (gallery.length === 0) return;
    setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
  };

  const handlePrevImage = () => {
    if (gallery.length === 0) return;
    setCurrentImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  const handleTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 35;
    if (distance > minSwipeDistance) {
      handleNextImage();
    } else if (distance < -minSwipeDistance) {
      handlePrevImage();
    }
  };

  const whatsappMsg = `Hi Nicole, I'm interested in the property listing #${property.id}: ${title} in ${area} (${price}). Could you please share more details?`;
  const whatsappUrl = `https://wa.me/${agent?.whatsappNumber || '60163288120'}?text=${encodeURIComponent(whatsappMsg)}`;

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location + ", Johor, Malaysia")}`;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: `Check out ${title} in ${location} on Nicole Pua Property Agent`,
        url: window.location.href,
      }).catch(() => { });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Property link copied to clipboard!");
    }
  };

  return (
    <div className="property-modal-overlay" onClick={onClose}>
      {/* Mobile Top Header Navigation */}
      <div className="property-mobile-header" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-header-left" onClick={onClose}>
          <button className="mobile-header-back" aria-label="Go back">
            <i className="bi bi-chevron-left"></i>
          </button>
          <div className="mobile-header-brand">
            <div className="mobile-brand-icon">
              <i className="bi bi-building-fill"></i>
            </div>
            <div className="mobile-brand-text">
              <div className="mobile-brand-name">NICOLE PUA</div>
              <div className="mobile-brand-sub">妮可地产经纪</div>
            </div>
          </div>
        </div>
        <div className="mobile-header-menu">
          <i className="bi bi-list"></i>
        </div>
      </div>

      {/* Mobile Breadcrumb (Visible only on mobile screens) */}
      <div className="property-mobile-breadcrumb" onClick={(e) => e.stopPropagation()}>
        <span>Home</span>
        <i className="bi bi-chevron-right"></i>
        <span>Areas</span>
        <i className="bi bi-chevron-right"></i>
        <span>{area}</span>
        <i className="bi bi-chevron-right"></i>
        <span className="active">{title}</span>
      </div>

      {/* Main Modal Card */}
      <div className="property-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Floating White Circular Close Button for Desktop */}
        <button
          className="modal-close-btn"
          onClick={onClose}
          aria-label="Close modal"
          title="Close"
        >
          <i className="bi bi-x"></i>
        </button>

        <div className="property-modal-content-grid">
          {/* LEFT COLUMN: Gallery, Actions, Description, Location */}
          <div className="modal-left-column">
            {/* Main Featured Image Carousel */}
            <div 
              className="modal-featured-image-wrapper"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="modal-image-badges-top-left">
                <span className={`modal-badge ${listingType === 'FOR RENT' ? 'rent' : 'sale'}`}>
                  {listingType}
                </span>
              </div>

              {listedDate && (
                <div className="modal-badge-listed-date">
                  <i className="bi bi-clock"></i> Listed on {listedDate}
                </div>
              )}

              {/* Mobile Image Counter Badge */}
              <div className="modal-mobile-image-counter">
                {currentImageIndex + 1} / {totalPhotosCount}
              </div>

              <img
                src={gallery[currentImageIndex] || mainImage}
                alt={title}
                className="modal-featured-image"
              />

              {/* Gallery Navigation Arrows */}
              <button className="gallery-nav-btn prev" onClick={handlePrevImage} aria-label="Previous photo">
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="gallery-nav-btn next" onClick={handleNextImage} aria-label="Next photo">
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>

            {/* Thumbnail Strip */}
            {gallery.length > 1 && (
              <div className="modal-thumbnails-strip">
                {gallery.map((imgUrl, idx) => {
                  const isMoreTile = gallery.length > 5 && idx === 4 && currentImageIndex < 3;
                  return (
                    <div
                      key={idx}
                      ref={(el) => (thumbnailRefs.current[idx] = el)}
                      className={`thumbnail-item ${currentImageIndex === idx ? 'active' : ''} ${isMoreTile ? 'more-photos-tile' : ''}`}
                      onClick={() => setCurrentImageIndex(idx)}
                    >
                      <img src={imgUrl} alt={`Thumbnail ${idx + 1}`} />
                      {isMoreTile && (
                        <div className="more-photos-overlay">
                          <span className="plus-count">+{gallery.length - 4}</span>
                          <span className="more-text">More Photos</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Desktop Action Buttons Bar */}
            <div className="modal-actions-bar">
              <button className="modal-action-btn">
                <i className="bi bi-camera"></i> Photos ({totalPhotosCount})
              </button>
              <button className="modal-action-btn" onClick={handleShare}>
                <i className="bi bi-share"></i> Share
              </button>
              <button
                className={`modal-action-btn ${isSaved ? 'saved' : ''}`}
                onClick={() => setIsSaved(!isSaved)}
              >
                <i className={`bi ${isSaved ? 'bi-heart-fill' : 'bi-heart'}`}></i> Save
              </button>
            </div>

            {/* About This Property Section (Desktop & Mobile) */}
            <div className="modal-section-card desktop-about-section">
              <h3 className="modal-section-title">About This Property</h3>
              <div className="modal-description-content">
                {descriptionText.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Mobile About Section with optional Read More toggle */}
            <div className="modal-section-card mobile-about-section">
              <h3 className="modal-section-title">About This Property</h3>
              <div className={`modal-description-content ${hasMoreContent && !readMoreExpanded ? 'collapsed' : 'expanded'}`}>
                {hasMoreContent ? (
                  <>
                    <p>{descriptionParagraphs[0]}</p>
                    {readMoreExpanded && descriptionParagraphs.slice(1).map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </>
                ) : (
                  descriptionParagraphs.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))
                )}
              </div>
              {hasMoreContent && (
                <button
                  className="btn-read-more"
                  onClick={() => setReadMoreExpanded(!readMoreExpanded)}
                >
                  {readMoreExpanded ? (
                    <>Read Less <i className="bi bi-chevron-up"></i></>
                  ) : (
                    <>Read More <i className="bi bi-chevron-down"></i></>
                  )}
                </button>
              )}
            </div>

            {/* Location Section */}
            <div className="modal-section-card modal-location-section">
              <h3 className="modal-section-title">Location & Map</h3>
              <div className="modal-location-container">
                <div className="modal-map-iframe-wrapper">
                  <iframe
                    title={`Map location for ${location}`}
                    width="100%"
                    height="220"
                    style={{ border: 0, borderRadius: '8px', width: '100%', display: 'block' }}
                    loading="lazy"
                    allowFullScreen
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(location + ", Johor, Malaysia")}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
                  ></iframe>
                </div>
                <div className="modal-location-info-bar">
                  <div className="location-name">
                    <i className="bi bi-geo-alt-fill" style={{ color: '#d4af37', marginRight: '0.4rem' }}></i>
                    {location}
                  </div>
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="location-maps-link"
                  >
                    Open in Google Maps <i className="bi bi-box-arrow-up-right"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Price, Specs, Agent Card, Mortgage Calculator */}
          <div className="modal-right-column">
            {/* Header Specs Info */}
            <div className="modal-header-specs">
              <div className="modal-price">{price}</div>
              <h2 className="modal-title">{title}</h2>
              <div className="modal-location-sub">{location}</div>

              {/* Dynamic Tags from Item DB */}
              {finalTags.length > 0 && (
                <div className="modal-tags-row">
                  {finalTags.map((tag, idx) => (
                    <span className="modal-tag-pill" key={idx}>{tag}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Property Details Section */}
            {propertyDetailsList.length > 0 && (
              <div className="modal-details-section">
                <h3 className="modal-details-title">Property Details</h3>
                <div className="modal-details-list">
                  {propertyDetailsList.map((item, idx) => (
                    <div className="modal-detail-row" key={idx}>
                      <div className="detail-left">
                        <i className={`bi ${item.icon} detail-icon`}></i>
                        <span className="detail-label">{item.label}</span>
                      </div>
                      <div className="detail-right">
                        <span className="detail-value">{item.value}</span>
                        <i className="bi bi-chevron-right mobile-only-chevron"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Agent Contact Card */}
            <div className="modal-agent-card">
              <div className="agent-card-profile">
                <img
                  src={ListingService.formatImageUrl(agent?.photoUrl || "/api/app-images/wa_profile.jpg")}
                  alt={agent?.name || "Nicole Pua"}
                  className="agent-card-avatar"
                />
                <div className="agent-card-meta">
                  <h4 className="agent-card-name">{agent?.name || "Nicole Pua"}</h4>
                  <div className="agent-card-role">{agent?.title || "Property Agent"}</div>
                  <div className="agent-card-contact-item">
                    <i className="bi bi-telephone-fill"></i> +6016-3288 120
                  </div>
                  <div className="agent-card-contact-item">
                    <i className="bi bi-envelope-fill"></i> nicolepua.property@gmail.com
                  </div>
                </div>
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-modal-whatsapp"
              >
                <i className="bi bi-whatsapp"></i> WhatsApp Me
              </a>
            </div>

            {/* Mortgage Calculator Widget (Desktop Only, Sale Only) */}
            {!isRent && (
              <div className="modal-mortgage-card">
                <h3 className="mortgage-card-title">Mortgage Calculator</h3>
                <div className="mortgage-card-sub">Est. Monthly Installment</div>
                <div className="mortgage-card-amount">RM 2,780</div>
                <button
                  className="btn-mortgage-calc"
                  onClick={() => alert("Estimated Monthly Installment based on 35-year tenure at 4.2% interest rate: RM 2,780/month.")}
                >
                  Calculate Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailModal;
